import { ChangeEvent, useEffect, useState } from 'react';

import { useMeasure } from '@uidotdev/usehooks';
import { get, mean } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';

import { orange } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';

import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import { BarSeriesType } from '@mui/x-charts';
import { BarPlot } from '@mui/x-charts/BarChart';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ResponsiveChartContainer } from '@mui/x-charts/ResponsiveChartContainer';

import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';

import { shadeHexColor } from '@/utils';

import dayjs from '@/dayjs';

import {
  EnergyUsage,
  useBreakpoints,
  useFormatters,
  usePostEnergyUsage,
} from '@/hooks';

import {
  EnergyUsageDataKey,
  OperatingPhase,
  getEnergyUsageInputFromFilters,
  store,
} from '../store';

export const EnergyUsageChartContainerHeight = 500;

function EnergyUsageChart() {
  const { t } = useTranslation();
  const theme = useTheme();
  const snap = useSnapshot(store);
  const { isMobileDevice } = useBreakpoints();

  // used to adjust centering of element in the chart area (loading, message, ...)
  const [titleRef, { height: titleHeight }] = useMeasure();

  // generic number format
  const { formatFloat } = useFormatters();

  const params = getEnergyUsageInputFromFilters(snap as typeof store);
  const [energyUsageByMonthResult, refresh] = usePostEnergyUsage(params);

  // get our dataset if not loaded yet
  const dataset: EnergyUsage[] = energyUsageByMonthResult.data || [];

  const totalAverage = mean(
    dataset
      // filter null values for empty months
      .filter((d: EnergyUsage) => get(d, snap.viewModeDataKey) !== null)
      .map((d: EnergyUsage) => get(d, snap.viewModeDataKey)),
  );

  // emission reference line value
  const emissionsReferenceValue = snap.isAveragePowerMode
    ? snap.globals.emissionsReference.energy
    : snap.globals.emissionsReference.co2e;

  const isLoading = energyUsageByMonthResult.isLoading;

  const noData = !dataset.length;

  const [viewOperatingPhases, setViewOperatingPhases] = useState(false);
  const handleChangeViewOperatingPhases = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setViewOperatingPhases(event.target.checked);
  };

  const series: BarSeriesType[] = [];

  if (!viewOperatingPhases || snap.operatingPhase !== OperatingPhase.ALL) {
    series.push({
      type: 'bar',
      dataKey: snap.viewModeDataKey,
      color: snap.isAveragePowerMode
        ? theme.palette.primary.main
        : theme.palette.secondary.main,
      valueFormatter: (value) => {
        return value !== null
          ? `${formatFloat(value)} ${snap.viewModeUnit}`
          : '';
      },
    });
  } else {
    let dataKey: EnergyUsageDataKey = snap.isAveragePowerMode
      ? 'active_power_kW'
      : 'co2eq_kg_per_hour';

    series.push({
      type: 'bar',
      stack: dataKey,
      dataKey: `${dataKey}_stable`,
      color: snap.isAveragePowerMode
        ? theme.palette.primary.main
        : theme.palette.secondary.main,
      valueFormatter: (value: number | null) => {
        return value !== null
          ? `Stable: ${formatFloat(value)} ${snap.viewModeUnit}`
          : '';
      },
    });
    series.push({
      type: 'bar',
      stack: dataKey,
      dataKey: `${dataKey}_transition`,
      color: shadeHexColor(
        snap.isAveragePowerMode
          ? theme.palette.primary.main
          : theme.palette.secondary.main,
        // snap.operatingPhase === OperatingPhase.ALL ? 0.3 : 0,
        0.25,
      ),
      valueFormatter: (value: number | null) => {
        return value !== null
          ? `Transition: ${formatFloat(value)} ${snap.viewModeUnit}`
          : '';
      },
    });
  }

  return (
    <Paper
      variant="elevation"
      sx={{
        height: `${EnergyUsageChartContainerHeight}px`,
        width: '100%',
        borderRadius: 4,
      }}
    >
      <Stack
        ref={titleRef}
        direction="row"
        alignItems="flex-start"
        justifyContent="flex-start"
        flexGrow={1}
        spacing={1}
        sx={{ pl: 3, pt: 2 }}
      >
        <Typography variant="h5"> {t(`viewMode.${snap.viewMode}`)}</Typography>
        {!isNaN(totalAverage) && (
          <Typography variant="h5" color="GrayText" sx={{ flexGrow: 1 }}>
            {totalAverage.toFixed(1)} {snap.viewModeUnit}
          </Typography>
        )}
        {snap.operatingPhase === OperatingPhase.ALL && (
          <FormControlLabel
            control={
              <Switch
                color={snap.isAveragePowerMode ? 'primary' : 'secondary'}
                onChange={handleChangeViewOperatingPhases}
              />
            }
            checked={viewOperatingPhases}
            label="View operating phases"
            labelPlacement="end"
            sx={{ pr: 4, display: 'none' }}
          />
        )}
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        sx={{
          width: '100%',
          height: EnergyUsageChartContainerHeight - (titleHeight || 0),
        }}
      >
        {isLoading && (
          <CircularProgress size={64} sx={{ mt: `-${titleHeight}px` }} />
        )}

        {!isLoading && noData && (
          <Typography variant="overline" sx={{ mt: `-${titleHeight}px` }}>
            {t('noData')}
          </Typography>
        )}

        {!isLoading && !noData && (
          <ResponsiveChartContainer
            dataset={dataset}
            sx={{ opacity: isLoading ? 0.4 : 1 }}
            margin={{
              top: 30,
              left: isMobileDevice ? 40 : 60,
              right: isMobileDevice ? 20 : 40,
            }}
            series={series}
            xAxis={[
              {
                dataKey: 'month',
                scaleType: 'band',
                id: 'x-axis-id',
                valueFormatter: (value, context) => {
                  const dt = dayjs(value);
                  return dt.format(
                    context.location === 'tick' ? 'MMM YYYY' : 'MMMM YYYY',
                  );
                },
              },
            ]}
          >
            <BarPlot borderRadius={4} tooltip={{ trigger: 'axis' }} />
            <ChartsXAxis position="bottom" axisId="x-axis-id" />
            <ChartsYAxis position="left" />
            <ChartsTooltip />
            {/* <ChartsAxisHighlight x="band" /> */}

            {/* emissions reference line */}
            {dataset.length > 0 && emissionsReferenceValue !== null && (
              <>
                <defs>
                  <filter
                    id="ref-line-background"
                    x="-5%"
                    width="110%"
                    y="-10%"
                    height="120%"
                  >
                    <feFlood floodColor={orange[900]} />
                    <feGaussianBlur stdDeviation="2" />
                    <feComponentTransfer>
                      <feFuncA type="table" tableValues="0 0 0 1" />
                    </feComponentTransfer>
                    <feComponentTransfer>
                      <feFuncA type="table" tableValues="0 1 1 1 1 1 1 1" />
                    </feComponentTransfer>
                    <feComposite operator="over" in="SourceGraphic" />
                  </filter>
                </defs>
                <ChartsReferenceLine
                  y={emissionsReferenceValue}
                  label={`Emissions reference : ${emissionsReferenceValue}`}
                  labelAlign="start"
                  lineStyle={{
                    strokeDasharray: '4 4',
                    stroke: orange[900],
                  }}
                  labelStyle={{
                    fontSize: 12,
                    fill: 'white',
                    filter: 'url(#ref-line-background)',
                  }}
                  spacing={{ x: 30, y: -4 }}
                />
              </>
            )}
          </ResponsiveChartContainer>
        )}
      </Stack>
    </Paper>
  );
}

export { EnergyUsageChart };
